import axios from 'axios';

const responseCache = new Map();
const pendingRequests = new Map();

const createCacheKey = (url, method, params = {}, data = {}) =>
  `${method.toUpperCase()}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`;

const removePendingRequest = (requestId) => {
  const controller = pendingRequests.get(requestId);
  if (controller) {
    controller.abort();
    pendingRequests.delete(requestId);
  }
};

export const cancelRequestById = (requestId) => {
  if (!requestId) return;
  removePendingRequest(requestId);
};

export const cancelRequestsByPattern = (pattern) => {
  if (!pattern) return;
  for (const key of Array.from(pendingRequests.keys())) {
    if (key.includes(pattern)) {
      removePendingRequest(key);
    }
  }
};

export const cancelAllRequests = () => {
  for (const key of Array.from(pendingRequests.keys())) {
    removePendingRequest(key);
  }
};

export const clearCache = (pattern = null) => {
  if (!pattern) {
    responseCache.clear();
    return;
  }
  for (const key of Array.from(responseCache.keys())) {
    if (key.includes(pattern)) {
      responseCache.delete(key);
    }
  }
};

const isCancellationError = (error) => {
  if (!error) return false;
  return (
    axios.isCancel?.(error) ||
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError' ||
    error.message === 'canceled'
  );
};

export const smartFetch = async ({
  url,
  method = 'get',
  params = {},
  data = {},
  headers = {},
  baseURL = '',
  fullUrl = false,
  useCache = false,
  cacheTTL = 120000,
  cacheKey: providedCacheKey,
  requestId: providedRequestId,
  axiosInstance = axios,
  axiosConfig = {},
}) => {
  if (!url) throw new Error('smartFetch requires a valid URL');

  const normalizedMethod = method.toLowerCase();
  const resolvedUrl = fullUrl ? url : `${baseURL ?? ''}${url}`;
  const cacheKey =
    useCache && normalizedMethod === 'get'
      ? providedCacheKey ||
        createCacheKey(resolvedUrl, normalizedMethod, params, data)
      : null;

  const requestId =
    providedRequestId || cacheKey || `${normalizedMethod}:${resolvedUrl}`;

  if (cacheKey && responseCache.has(cacheKey)) {
    const cachedResponse = responseCache.get(cacheKey);
    if (cachedResponse.expiry > Date.now()) {
      return cachedResponse.response;
    }
    responseCache.delete(cacheKey);
  }

  removePendingRequest(requestId);

  const controller = new AbortController();
  pendingRequests.set(requestId, controller);

  const { signal: externalSignal, ...restAxiosConfig } = axiosConfig ?? {};
  const combinedSignal =
    externalSignal && typeof AbortSignal.any === 'function'
      ? AbortSignal.any([controller.signal, externalSignal])
      : controller.signal;

  const requestOptions = {
    url: resolvedUrl,
    method: normalizedMethod,
    params,
    data,
    headers,
    ...restAxiosConfig,
    signal: combinedSignal,
  };

  try {
    const response = await axiosInstance(requestOptions);

    if (cacheKey) {
      responseCache.set(cacheKey, {
        response,
        expiry: Date.now() + cacheTTL,
      });
    }

    pendingRequests.delete(requestId);
    return response;
  } catch (error) {
    pendingRequests.delete(requestId);
    if (isCancellationError(error)) {
      return error;
    }
    throw error;
  }
};

export default smartFetch;
